import { createContext, createEffect, createSignal, Show, Suspense, useContext } from 'solid-js';
import { useAnalytics } from '@troon/analytics';
import { setUser } from '@sentry/solidstart';
import { query, createAsync } from '@solidjs/router';
import { cachedGet } from '@troon/api-client';
import {
	DialogDeprecated,
	DialogActionDeprecated,
	DialogActionsDeprecated,
	DialogContentDeprecated,
	Link,
} from '@troon/ui';
import type { ApiResponse } from '@troon/api-client';
import type { AccessorWithLatest } from '@solidjs/router';
import type { Accessor, ParentProps, Setter } from 'solid-js';

export type User = ApiResponse<'get', '/v0/auth/me'>;
type CTX = {
	user: AccessorWithLatest<User | null>;
	// Whether the user attempted login or not
	attemptedLogin: Accessor<boolean>;
	setAttemptedLogin: Setter<boolean>;
};

const User = createContext<CTX>();

export function UserProvider(props: ParentProps) {
	const user = createAsync(() => getLoggedInUser(), { deferStream: true });
	const [attemptedLogin, setAttemptedLogin] = createSignal(false);
	const analytics = useAnalytics();

	createEffect(() => {
		const data = user();

		if (data) {
			const fullName = `${data.firstName} ${data.lastName}`;
			setUser({ id: data.id, email: data.email, fullName });

			if (!analytics?._isIdentified()) {
				// Important: do not call identify if already identified
				// Failing to do so will add at least n+1 calls to /decide (the feature flag endpoint)
				analytics?.identify(data.id, {
					email: data.email,
					fullName,
					accessSubscription: data.troonAccessProductType,
					rewardsId: data.troonRewardsId,
				});
			}
		} else if (analytics?._isIdentified()) {
			// log out if was previously logged in & identified
			analytics?.reset();
		}
	});

	return (
		<Suspense>
			<Show when={user() !== undefined}>
				<User.Provider value={{ user: user as AccessorWithLatest<User | null>, attemptedLogin, setAttemptedLogin }}>
					{props.children}
					<DialogDeprecated key="cookie-problem" open={attemptedLogin() && !user()}>
						<DialogContentDeprecated closeButton={false}>
							<p>
								Your browser is blocking third-party cookies, which are required for logging in. Please enable cookies
								or continue booking in a new window.
							</p>
							<DialogActionsDeprecated>
								<DialogActionDeprecated as={Link} target="_blank" href={window.location.href}>
									Open in New Window
								</DialogActionDeprecated>
							</DialogActionsDeprecated>
						</DialogContentDeprecated>
					</DialogDeprecated>
				</User.Provider>
			</Show>
		</Suspense>
	);
}

export function useUser() {
	const ctx = useContext(User);
	if (!ctx) {
		throw new Error('User context not initialized');
	}
	return ctx.user;
}

export function useAttemptedLogin(): [Accessor<boolean>, Setter<boolean>] {
	const ctx = useContext(User);
	if (!ctx) {
		throw new Error('User context not initialized');
	}
	return [ctx.attemptedLogin, ctx.setAttemptedLogin];
}

// Manually reimplement lazy/minimal cachedGet
const getLoggedInUser = query(async (): Promise<User | null> => {
	try {
		const user = await getUser({});
		return user || null;
	} catch {
		return null;
	}
}, '/v0/auth/me');

const getUser = cachedGet('/v0/auth/me', {}, { redirect401: false });
